import React, { useState, useEffect } from 'react';
import { X, Book, FileText, Link2, Download, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEntity } from '@/hooks/useEntity';
import { appIntegrationEntityConfig, type AppIntegration as AppIntegrationType } from '@/entities/AppIntegration';
import { journalEntryEntityConfig, type JournalEntry as JournalEntryType } from '@/entities/Journal';

type AppConnection = {
  id: string;
  name: string;
  icon: string;
  connected: boolean;
  description: string;
  loginUrl?: string;
};

type AppsIntegrationPanelProps = {
  onClose: () => void;
};

const AppsIntegrationPanel: React.FC<AppsIntegrationPanelProps> = ({ onClose }) => {
  const { user } = useAuth();
  const { items: dbApps, create: createApp, update: updateApp, reload: reloadApps } = useEntity<AppIntegrationType>(appIntegrationEntityConfig);
  const { items: dbJournalEntries, reload: reloadJournalEntries } = useEntity<JournalEntryType>(journalEntryEntityConfig);
  
  const [apps, setApps] = useState<AppConnection[]>([
    {
      id: 'kobo',
      name: 'Kobo',
      icon: '📚',
      connected: false,
      description: 'Connect to your Kobo account to export journals and documents as EPUB files',
      loginUrl: 'https://authorize.kobo.com/signin',
    },
    {
      id: 'kindle',
      name: 'Kindle',
      icon: '📖',
      connected: false,
      description: 'Connect to Amazon Kindle to send documents directly to your Kindle library',
      loginUrl: 'https://www.amazon.com/ap/signin?openid.pape.max_auth_age=0&openid.return_to=https%3A%2F%2Fwww.amazon.com%2Fmn%2Fdcw&openid.identity=http%3A%2F%2Fspecs.openid.net%2Fauth%2F2.0%2Fidentifier_select&openid.assoc_handle=amzn_device_na&openid.mode=checkid_setup&openid.claimed_id=http%3A%2F%2Fspecs.openid.net%2Fauth%2F2.0%2Fidentifier_select&openid.ns=http%3A%2F%2Fspecs.openid.net%2Fauth%2F2.0',
    },
    {
      id: 'google-drive',
      name: 'Google Drive',
      icon: '☁️',
      connected: false,
      description: 'Backup your journals and documents to Google Drive automatically',
    },
    {
      id: 'dropbox',
      name: 'Dropbox',
      icon: '📦',
      connected: false,
      description: 'Sync your work with Dropbox for easy access across devices',
    },
  ]);

  const [showExportOptions, setShowExportOptions] = useState<string | null>(null);

  // Load app connections from database
  useEffect(() => {
    reloadApps();
  }, [reloadApps]);

  useEffect(() => {
    if (!user) return;
    
    // Load connected apps from database
    const userApps = dbApps.filter(a => a.userId === user.id);
    
    // Update apps state with connected status from database
    setApps(prev => prev.map(app => {
      const dbApp = userApps.find(ua => ua.appId === app.id);
      return dbApp ? { ...app, connected: dbApp.connected } : app;
    }));
  }, [dbApps, user]);

  const handleConnect = async (appId: string) => {
    const app = apps.find(a => a.id === appId);
    
    if (!user) {
      alert('You must be logged in to connect apps');
      return;
    }

    // For cloud storage, use simulated connection (no OAuth needed)
    if (appId === 'google-drive' || appId === 'dropbox') {
      // Simulate connection immediately
      setApps(prev => prev.map(a => 
        a.id === appId ? { ...a, connected: true } : a
      ));
      
      // Save to database
      const dbApp: Partial<AppIntegrationType> = {
        userId: user.id,
        appId,
        appName: app?.name || appId,
        connected: true,
      };
      await createApp(dbApp as any);
      
      alert(`✅ ${app?.name} connected!\n\n🔐 Note: This is a simulated connection for demonstration.\n📤 You can now use the "Backup Now" feature to download your data.`);
      return;
    }
    
    // For e-readers, use OAuth popup
    if (app?.loginUrl) {
      // Open login in new window
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      window.open(
        app.loginUrl,
        'Connect to ' + app.name,
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      // Simulate connection (in real app, this would be OAuth callback)
      setTimeout(async () => {
        setApps(prev => prev.map(a => 
          a.id === appId ? { ...a, connected: true } : a
        ));
        
        // Save to database
        const dbApp: Partial<AppIntegrationType> = {
          userId: user.id,
          appId,
          appName: app?.name || appId,
          connected: true,
        };
        await createApp(dbApp as any);
      }, 2000);
    } else {
      alert('Integration coming soon!');
    }
  };

  const handleDisconnect = async (appId: string) => {
    if (window.confirm('Disconnect this app?')) {
      if (!user) return;
      
      setApps(prev => prev.map(a => 
        a.id === appId ? { ...a, connected: false } : a
      ));
      
      // Find and update in database
      const dbApp = dbApps.find(a => a.userId === user.id && a.appId === appId);
      if (dbApp) {
        await updateApp(dbApp.id, { ...dbApp, connected: false } as any);
      }
    }
  };
  const handleExport = async (appId: string, format: string) => {
    if (!user) {
      alert('You must be logged in to export.');
      return;
    }

    // Load all journal entries from database for this user
    await reloadJournalEntries();
    
    const allEntries = dbJournalEntries
      .filter((e: any) => e.userId === user.id)
      .map(e => ({
        title: e.title,
        date: e.entryDate,
        time: e.entryTime,
        moduleNumber: e.moduleNumber,
        dueDate: e.dueDate,
        content: e.content,
        entryType: e.entryType,
      }));
    
    if (!allEntries || allEntries.length === 0) {
      alert('No journal entries found to export. Please create some journal or discussion entries first.');
      return;
    }

    try {
      if (format === 'EPUB') {
        exportAsEPUB(allEntries, appId);
      } else if (format === 'MOBI') {
        exportAsMOBI(allEntries, appId);
      } else if (format === 'PDF') {
        exportAsPDF(allEntries, appId);
      }
      
      setShowExportOptions(null);
    } catch (e) {
      console.error('Export failed:', e);
      alert('Failed to export. Please try again.');
    }
  };

  const exportAsEPUB = (entries: any[], appId: string) => {
    // Create EPUB-compatible HTML
    const htmlContent = `
      <?xml version="1.0" encoding="UTF-8"?>
      <!DOCTYPE html>
      <html xmlns="http://www.w3.org/1999/xhtml">
      <head>
        <title>Journal Entries</title>
        <style>
          body { font-family: Georgia, serif; line-height: 1.6; margin: 2em; }
          h1 { color: #4c1d95; border-bottom: 2px solid #4c1d95; padding-bottom: 0.5em; }
          h2 { color: #6d28d9; margin-top: 2em; }
          .meta { background: #f3f4f6; padding: 1em; margin: 1em 0; border-radius: 0.5em; }
          .content { margin: 1.5em 0; white-space: pre-wrap; }
          .entry { page-break-after: always; margin-bottom: 3em; }
        </style>
      </head>
      <body>
        <h1>Journal & Discussion Entries</h1>
        ${entries.map((entry, index) => `
          <div class="entry">
            <h2>${index + 1}. ${entry.title}</h2>
            <div class="meta">
              <p><strong>Type:</strong> ${entry.entryType === 'discussion' ? 'Discussion' : 'Journal'}</p>
              <p><strong>Date:</strong> ${new Date(entry.date).toLocaleDateString()}</p>
              <p><strong>Time:</strong> ${entry.time}</p>
              ${entry.moduleNumber ? `<p><strong>Module:</strong> ${entry.moduleNumber}</p>` : ''}
              ${entry.dueDate ? `<p><strong>Due Date:</strong> ${new Date(entry.dueDate).toLocaleDateString()}</p>` : ''}
            </div>
            <div class="content">${entry.content}</div>
          </div>
        `).join('')}
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/epub+zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `journal_entries_${new Date().toISOString().split('T')[0]}.epub`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    alert(`EPUB file downloaded! Transfer it to your ${appId === 'kobo' ? 'Kobo' : 'Kindle'} device via USB or email.`);
  };

  const exportAsMOBI = (entries: any[], appId: string) => {
    // MOBI format (simplified HTML for Kindle)
    const htmlContent = `
      <html>
      <head>
        <meta charset="utf-8">
        <title>Journal Entries</title>
      </head>
      <body>
        <h1>Journal & Discussion Entries</h1>
        <mbp:pagebreak/>
        ${entries.map((entry, index) => `
          <h2>${index + 1}. ${entry.title}</h2>
          <p><b>Type:</b> ${entry.entryType === 'discussion' ? 'Discussion' : 'Journal'}</p>
          <p><b>Date:</b> ${new Date(entry.date).toLocaleDateString()}</p>
          <p><b>Time:</b> ${entry.time}</p>
          ${entry.moduleNumber ? `<p><b>Module:</b> ${entry.moduleNumber}</p>` : ''}
          ${entry.dueDate ? `<p><b>Due Date:</b> ${new Date(entry.dueDate).toLocaleDateString()}</p>` : ''}
          <p>${entry.content.replace(/\n/g, '<br>')}</p>
          <mbp:pagebreak/>
        `).join('')}
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/x-mobipocket-ebook' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `journal_entries_${new Date().toISOString().split('T')[0]}.mobi`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    alert('MOBI file downloaded! Email it to your Kindle address or transfer via USB.');
  };

  const exportAsPDF = (entries: any[], appId: string) => {
    // Create HTML for PDF printing
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Journal Entries</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
          h1 { color: #4c1d95; border-bottom: 3px solid #4c1d95; padding-bottom: 10px; }
          h2 { color: #6d28d9; page-break-before: always; margin-top: 40px; }
          .meta { background: #f3f4f6; padding: 15px; border-radius: 8px; margin: 20px 0; }
          .content { margin-top: 20px; white-space: pre-wrap; }
          @media print { h2 { page-break-before: always; } }
        </style>
      </head>
      <body>
        <h1>Journal & Discussion Entries</h1>
        ${entries.map((entry, index) => `
          <h2>${index + 1}. ${entry.title}</h2>
          <div class="meta">
            <p><strong>Type:</strong> ${entry.entryType === 'discussion' ? 'Discussion' : 'Journal'}</p>
            <p><strong>Date:</strong> ${new Date(entry.date).toLocaleDateString()}</p>
            <p><strong>Time:</strong> ${entry.time}</p>
            ${entry.moduleNumber ? `<p><strong>Module:</strong> ${entry.moduleNumber}</p>` : ''}
            ${entry.dueDate ? `<p><strong>Due Date:</strong> ${new Date(entry.dueDate).toLocaleDateString()}</p>` : ''}
          </div>
          <div class="content">${entry.content.replace(/\n/g, '<br>')}</div>
        `).join('')}
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const handleOpenAccount = (appId: string) => {
    if (appId === 'kobo') {
      window.open('https://www.kobo.com/us/en/mybooks', '_blank');
    } else if (appId === 'kindle') {
      window.open('https://read.amazon.com/notebook', '_blank');
    } else if (appId === 'google-drive') {
      window.open('https://drive.google.com', '_blank');
    } else if (appId === 'dropbox') {
      window.open('https://www.dropbox.com/home', '_blank');
    }
  };

  const handleUploadToDrive = async (appId: string) => {
    const app = apps.find(a => a.id === appId);
    if (!app?.connected) {
      alert('Please connect your account first!');
      return;
    }

    if (!user) {
      alert('You must be logged in to backup data.');
      return;
    }

    // Load all journal entries from database for this user
    await reloadJournalEntries();
    
    const allEntries = dbJournalEntries
      .filter((e: any) => e.userId === user.id)
      .map(e => ({
        id: e.id,
        title: e.title,
        date: e.entryDate,
        time: e.entryTime,
        moduleNumber: e.moduleNumber,
        dueDate: e.dueDate,
        content: e.content,
        images: e.images,
        entryType: e.entryType,
        created_at: e.createdAt,
        updated_at: e.updatedAt,
      }));

    if (allEntries.length === 0) {
      alert('No content to upload!');
      return;
    }

    // Create a JSON file with all content
    const exportData = {
      exported_at: new Date().toISOString(),
      app: 'WHIPT Organizer',
      total_entries: allEntries.length,
      entries: allEntries,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const fileName = `whipt_backup_${new Date().toISOString().split('T')[0]}.json`;

    if (appId === 'google-drive') {
      // For now, download the file and instruct user to upload
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert(`✅ Backup file downloaded!\n\n📁 File: ${fileName}\n📤 Please upload this file to your Google Drive manually.\n\n💡 In the future, this will auto-upload to your Drive!`);
    } else if (appId === 'dropbox') {
      // For now, download the file and instruct user to upload
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert(`✅ Backup file downloaded!\n\n📁 File: ${fileName}\n📤 Please upload this file to your Dropbox manually.\n\n💡 In the future, this will auto-upload to Dropbox!`);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-purple-400/30 rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-purple-400/30">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Link2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">App Integrations</h2>
              <p className="text-sm text-purple-200">Connect your favorite apps and e-readers</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {/* E-Readers Section */}
            <div>
              <h3 className="text-lg font-bold text-purple-200 mb-3 flex items-center gap-2">
                <Book className="w-5 h-5" />
                E-Readers
              </h3>
              <div className="grid gap-4">
                {apps.filter(app => app.id === 'kobo' || app.id === 'kindle').map(app => (
                  <div
                    key={app.id}
                    className="rounded-2xl bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border border-purple-400/20 p-5"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="text-4xl">{app.icon}</div>
                        <div>
                          <h4 className="text-lg font-bold text-white flex items-center gap-2">
                            {app.name}
                            {app.connected && (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 text-xs font-semibold flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Connected
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-slate-300/80">{app.description}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      {!app.connected ? (
                        <button
                          onClick={() => handleConnect(app.id)}
                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold text-sm transition-all flex items-center gap-2"
                        >
                          <Link2 className="w-4 h-4" />
                          Connect {app.name}
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleOpenAccount(app.id)}
                            className="px-4 py-2 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 font-semibold text-sm transition-all flex items-center gap-2"
                          >
                            <Book className="w-4 h-4" />
                            Open {app.name}
                          </button>
                          <button
                            onClick={() => setShowExportOptions(showExportOptions === app.id ? null : app.id)}
                            className="px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 font-semibold text-sm transition-all flex items-center gap-2"
                          >
                            <Download className="w-4 h-4" />
                            Export Journals
                          </button>
                          <button
                            onClick={() => handleDisconnect(app.id)}
                            className="px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-200 font-semibold text-sm transition-all"
                          >
                            Disconnect
                          </button>
                        </>
                      )}
                    </div>

                    {/* Export Options Dropdown */}
                    {showExportOptions === app.id && app.connected && (
                      <div className="mt-3 p-3 rounded-xl bg-black/30 border border-purple-400/20">
                        <p className="text-xs text-purple-200 mb-2 font-semibold">Select export format:</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleExport(app.id, 'EPUB')}
                            className="flex-1 px-3 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-sm font-semibold transition-all"
                          >
                            📘 EPUB
                          </button>
                          {app.id === 'kindle' && (
                            <button
                              onClick={() => handleExport(app.id, 'MOBI')}
                              className="flex-1 px-3 py-2 rounded-lg bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 text-sm font-semibold transition-all"
                            >
                              📙 MOBI
                            </button>
                          )}
                          <button
                            onClick={() => handleExport(app.id, 'PDF')}
                            className="flex-1 px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-sm font-semibold transition-all"
                          >
                            📕 PDF
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Cloud Storage Section */}
            <div className="mt-6">
              <h3 className="text-lg font-bold text-purple-200 mb-3 flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Cloud Storage
              </h3>
              <div className="grid gap-4">
                {apps.filter(app => app.id === 'google-drive' || app.id === 'dropbox').map(app => (
                  <div
                    key={app.id}
                    className="rounded-2xl bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border border-cyan-400/20 p-5"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-4xl">{app.icon}</div>
                        <div>
                          <h4 className="text-lg font-bold text-white flex items-center gap-2">
                            {app.name}
                            {app.connected && (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 text-xs font-semibold flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Connected
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-slate-300/80">{app.description}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      {!app.connected ? (
                        <button
                          onClick={() => handleConnect(app.id)}
                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold text-sm transition-all flex items-center gap-2"
                        >
                          <Link2 className="w-4 h-4" />
                          Connect {app.name}
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleOpenAccount(app.id)}
                            className="px-4 py-2 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 font-semibold text-sm transition-all flex items-center gap-2"
                          >
                            <FileText className="w-4 h-4" />
                            Open {app.name}
                          </button>
                          <button
                            onClick={() => handleUploadToDrive(app.id)}
                            className="px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 font-semibold text-sm transition-all flex items-center gap-2"
                          >
                            <Upload className="w-4 h-4" />
                            Backup Now
                          </button>
                          <button
                            onClick={() => handleDisconnect(app.id)}
                            className="px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-200 font-semibold text-sm transition-all"
                          >
                            Disconnect
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-6 rounded-xl bg-blue-900/20 border border-blue-400/30 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-300 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-200">
                  <p className="font-semibold mb-1">About App Integrations</p>
                  <ul className="space-y-1 text-blue-200/80">
                    <li>• Export your journals and documents directly to your e-reader</li>
                    <li>• Kobo and Kindle support EPUB, MOBI, and PDF formats</li>
                    <li>• Cloud storage keeps your work backed up automatically</li>
                    <li>• All connections are secure and can be revoked anytime</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppsIntegrationPanel;
